'use client';
import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import ToolTipIcon from '@/components/customercenter-ui/svgs/tooltipIcon';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { formatMobileNumber, getCookieValue, createUrl } from '@/helpers/utils/siteUtils';
import { CUSTOMER_DETAILS_PAGE_PATH, COOKIE_KEYS, SITE_KEY_MAP_MO } from '../../../../../constants/constant';
import DivisionsColumn from '../../search/components/common/table/columns/divisions';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import AddUserToDivisionDialog from '../common/AddUserToDivision';

const CustomerResultQuickView = ({
  data,
  Texts,
  divisionData,
  allRowsData,
  setAllRowsDataFuncRef,
  mailingRecordId,
}: any) => {
  const quickViewData = data?.[0];
  const projectSettings: ProjectSettings = useProjSettings();
  const [state, setState] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [AddUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [currentSite, setCurrentSite] = useState('ZBSite');
  const [selectedDivisionCode, setSelectedDivisionCode] = useState('ZB');

  useEffect(() => {
    const currentSite = getCookieValue(COOKIE_KEYS?.selectedSite) ?? 'ZBSite';
    const selectedDivisionCode = currentSite?.slice(0, 2) ?? 'ZB';
    setCurrentSite(currentSite);
    setSelectedDivisionCode(selectedDivisionCode);
  }, []);

  const isCustomerInDivision = useCallback(() => {
    if (!selectedDivisionCode) {
      return false;
    }
    return (
      quickViewData?.divisions?.divisionsData?.some((division: string) => division.startsWith(selectedDivisionCode)) ??
      false
    );
  }, [quickViewData?.divisions?.divisionsData?.length, currentSite]);

  const accountData = [
    { field: Texts?.accountNumber, value: quickViewData?.accountNumber, mask: true },
    { field: Texts?.firstName, value: quickViewData?.firstName, mask: true },
    { field: Texts?.lastName, value: quickViewData?.lastName, mask: true },
    { field: Texts?.companyName, value: quickViewData?.companyName, mask: true },
    { field: Texts?.primaryPhoneNumber, value: [formatMobileNumber(quickViewData?.phoneNumber)], mask: true },
    { field: Texts?.emailAddress, value: [quickViewData?.emailAddress], mask: true },
    { field: Texts?.dateOfBirth, value: quickViewData?.dateOfBirth, mask: true },
    { field: Texts?.ssn, value: quickViewData?.ssn, mask: true },
  ];
  const accountAddress = [
    { field: Texts?.addressLine1, value: quickViewData?.addressLine1, mask: true },
    { field: Texts?.addressLine2, value: quickViewData?.addressLine2, mask: true },
    { field: Texts?.city, value: quickViewData?.city, mask: true },
    { field: Texts?.state, value: state, mask: true },
    { field: Texts?.zipCode, value: quickViewData?.zipCode, mask: true },
    { field: Texts?.divisions, value: divisionData, mask: false },
  ];

  const customerDetailsLink = createUrl(
    quickViewData?.customerId,
    quickViewData?.accountNumber,
    selectedDivisionCode,
    '',
    mailingRecordId,
  );
  useEffect(() => {
    if (projectSettings) {
      const stateLocations = projectSettings?.shippingLocations
        ? JSON.parse(projectSettings?.shippingLocations)?.US
        : [];
      const currentState = stateLocations?.filter((items: any) => items.code === quickViewData?.state);
      if (quickViewData?.state && currentState) {
        setState(`${currentState?.[0]?.code} - ${currentState?.[0]?.name}`);
      } else {
        setState('');
      }
    }
  }, [projectSettings, quickViewData]);

  return (
    <Dialog>
      <DialogTrigger className="underline">{Texts?.tableRowQuickView}</DialogTrigger>
      <DialogContent className="mb-12 max-w-[900px] border-foggyGray bg-white sm:rounded-md">
        <DialogHeader>
          {/* chng-text-25px */}
          <DialogTitle className="text-2xl font-bold">
            {Texts?.customerDetailsHeading}
            {isCustomerInDivision() && (
              <Link
                className="ml-4 text-sm font-medium text-blueLight underline"
                href={`/${CUSTOMER_DETAILS_PAGE_PATH}/${customerDetailsLink}`}
              >
                {Texts?.fullView}
              </Link>
            )}
          </DialogTitle>
          {!isCustomerInDivision() ? (
            <DialogDescription className="flex items-center gap-2 pb-3 pt-5 text-sm font-bold">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="cursor-pointer">
                      <ToolTipIcon />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="bg-white">
                    <p>{Texts?.addToLibrary}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {Texts?.notPartOfDivision}
              <span
                className="cursor-pointer text-sm font-normal text-blueLight underline"
                onClick={() => setAddUserDialogOpen(true)}
              >
                <AddUserToDivisionDialog
                  Texts={Texts}
                  dialogTriggerText={Texts?.addUserToDivision}
                  allRowsData={allRowsData}
                  divisionToAdd={selectedDivisionCode}
                  dialogDescriptionText={`${Texts?.sureToAddCustomerToSite} ${
                    selectedDivisionCode &&
                    `${SITE_KEY_MAP_MO?.[selectedDivisionCode]}${selectedDivisionCode === 'ZD' ? '™' : ''}`
                  }?`}
                  setAllRowsDataFuncRef={setAllRowsDataFuncRef}
                  customerId={quickViewData?.accountNumber}
                  accountNumber={quickViewData?.accountNumber}
                />
              </span>
            </DialogDescription>
          ) : null}
        </DialogHeader>
        <div className="mt-4 grid grid-cols-2 gap-4 text-sm font-normal">
          <div>
            {accountData?.map((items, key) => (
              <div className="flex gap-2" key={key}>
                <div className="mb-4 flex-1 text-black">{items.field}</div>
                <div className="flex-1 break-all font-semibold">
                  {!items.value ? (
                    <p className="mb-1">{''}</p>
                  ) : typeof items.value === 'string' ? (
                    <p className={items?.mask ? 'gbmask' : ''}>{items.value}</p>
                  ) : (
                    <div>
                      <p className={items?.mask ? 'gbmask' : ''}>{items?.value?.[0] ?? ''}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div>
            {accountAddress?.map((items, key) => (
              <div className="flex gap-2" key={key}>
                <p className="mb-3 flex-1 font-normal">{items.field}</p>
                <div
                  className={items?.mask ? 'gbmask flex-1 break-all font-semibold' : 'flex-1 break-all font-semibold'}
                >
                  {!items.value ? (
                    <p className="mb-1">{''}</p>
                  ) : typeof items.value === 'string' ? (
                    items.value
                  ) : (
                    <DivisionsColumn
                      data={items?.value}
                      Texts
                      showAllDivsions
                      selectedCustomerData={quickViewData}
                      mailingRecordId={mailingRecordId}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerResultQuickView;
